<template>
  <teleport to="body">
    <div class="toast-container">
      <transition-group
        class="toast-group"
        name="toast"
        tag="div"
        @before-enter="beforeEnter"
        @enter="enter"
        @leave="leave"
      >
        <div v-for="item in toasts" :key="item.id" class="toast" :class="item.type">
          <img v-if="item.type !== 'error'" :src="imageDict[item.type]" style="margin-right: 5px" />
          <img v-if="item.type === 'error'" :src="errorIcon" style="margin-right: 5px" />
          <span>{{ item.message }}</span>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup lang="ts">
  import { App } from '@/app'
  import { ref, watch } from 'vue'
  import { type ToastInterface } from '@/app/type'
  import errorIcon from '@/assets/svg/error-white.svg'
  import succesIcon from '@/assets/svg/check-primary.svg'
  import loadingIcon from '@/assets/svg/loading-white.svg'
  import infoIcon from '@/assets/svg/info-white-icon.svg'

  const imageDict: Record<ToastInterface['type'], string> = {
    success: succesIcon,
    loading: loadingIcon,
    error: errorIcon,
    info: infoIcon,
    warning: errorIcon,
  }
  const toasts = ref<ToastInterface[]>(App.toastInstance.get())

  const animationDuration = 500

  function beforeEnter(element: Element): void {
    if (element instanceof HTMLElement) {
      element.style.transform = 'translateY(-20px)'
      element.style.opacity = '0'
    }
  }

  function enter(element: Element, done: () => void): void {
    if (element instanceof HTMLElement) {
      void element.offsetHeight
      element.style.transition = `transform ${animationDuration}ms ease, opacity ${animationDuration}ms ease`
      element.style.transform = 'translateY(0)'
      element.style.opacity = '1'
      setTimeout(done, animationDuration)
    }
  }

  function leave(element: Element, done: () => void): void {
    if (App.toastInstance.isResetting.value) {
      done()
      return
    }
    if (element instanceof HTMLElement) {
      const height = element.offsetHeight
      element.style.transition = `transform ${animationDuration}ms ease, opacity ${animationDuration}ms ease, margin-top ${animationDuration}ms ease`
      element.style.transform = 'translateY(-20px)'
      element.style.opacity = '0'
      element.style.marginTop = `-${height + 8}px`
      const duration =
        (element.dataset.duration && parseInt(element.dataset.duration)) || animationDuration
      setTimeout(done, duration)
    }
  }

  watch(
    () => App.toastInstance.get(),
    toast => {
      toasts.value = toast
    },
    { deep: true },
  )
</script>

<style lang="scss" scoped>
  .toast-container {
    position: fixed;
    top: 40px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999999;
    height: max-content;
    .toast-group {
      display: flex;
      flex-direction: column;
      align-items: center;
      .toast {
        margin-bottom: 8px;
        transition:
          transform 0.5s ease,
          opacity 0.5s ease,
          margin-top 0.2s ease; /* margin-top을 애니메이션에 추가 */
        border-radius: 0.75rem;
        opacity: 1;
        width: max-content;
        display: flex;
        align-items: center;
        padding: 8.5px 16px;
        background-color: #000;
        color: #fff;
        font-size: 0.87rem;
        font-weight: 600;
        box-shadow: 0px 3px 5px -1px #0000001a;
        img {
          width: 0.87rem;
          height: 0.87rem;
        }
        &.error {
          color: var(--White, #fff);
          border-color: var(--Red-500, #fe5051);
          background-color: var(--Red-500, #fe5051);
        }
      }
    }
  }
</style>
